/**
 * The eight lead fields an intake question can be mapped to via a form
 * item's `config.leadField`, and their display labels. Mirrors operations'
 * `LEAD_FIELD_TARGETS` in `forms-engine/support/lead-field-mapping.ts` —
 * keep the two lists in sync; nothing here reads or writes that mapping,
 * it only names the same eight targets for display.
 *
 * Shared by the lead's Form tab (the "→ Lead title" style hint on each
 * answer) and the template builder's "Maps to lead field" picker.
 */
export type LeadFieldTarget =
  | 'title'
  | 'description'
  | 'serviceRequested'
  | 'serviceCategoryId'
  | 'priority'
  | 'urgency'
  | 'estimatedValue'
  | 'followUpDate';

export const LEAD_FIELD_TARGETS = [
  'title',
  'description',
  'serviceRequested',
  'serviceCategoryId',
  'priority',
  'urgency',
  'estimatedValue',
  'followUpDate',
] as const satisfies readonly LeadFieldTarget[];

/**
 * Targets whose answer reaches the lead only through
 * `config.leadFieldOptionMap` (mirrors operations' `LEAD_FIELD_CHOICE_TARGETS`).
 */
export const LEAD_FIELD_CHOICE_TARGETS = [
  'serviceCategoryId',
  'priority',
  'urgency',
] as const satisfies readonly LeadFieldTarget[];

export function isLeadFieldChoiceTarget(target: string | null | undefined): boolean {
  return Boolean(target) && (LEAD_FIELD_CHOICE_TARGETS as readonly string[]).includes(target!);
}

export const LEAD_FIELD_TARGET_LABELS: Record<LeadFieldTarget, string> = {
  title: 'Lead title',
  description: 'Notes',
  serviceRequested: 'Service requested',
  serviceCategoryId: 'Service category',
  priority: 'Priority',
  urgency: 'Urgency',
  estimatedValue: 'Estimated value',
  followUpDate: 'Follow-up date',
};

function isLeadFieldTarget(value: string): value is LeadFieldTarget {
  return (LEAD_FIELD_TARGETS as readonly string[]).includes(value);
}

/** The display label for a `config.leadField` value, or undefined for anything else. */
export function leadFieldTargetLabel(target: string | null | undefined): string | undefined {
  if (!target || !isLeadFieldTarget(target)) return undefined;
  return LEAD_FIELD_TARGET_LABELS[target];
}
